import { useState, useRef } from "react";
import uplaodIcon from "../assets/upload-green-icon.svg";
import calenderIcon from "../assets/calender-icon.svg";
import timeIcon from "../assets/time-icon.svg";
import saveIcon from "../assets/save-icon.svg";

const YesNoField = ({ label, value, onChange }) => (
  <div className="w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[14px] pr-[4px] flex items-center gap-[8px]">
    <span className="font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#1A1A1A] whitespace-nowrap min-w-0 truncate">
      {label}
    </span>
    <div className="ml-auto flex items-center gap-[2px] shrink-0">
      <button
        type="button"
        onClick={() => onChange("Yes")}
        className={`box-border shrink-0 w-[58px] 3xl:w-[66px] h-[41px] rounded-[100px] px-0 py-0 border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
          value === "Yes"
            ? "bg-[#5AC69F] text-white"
            : "bg-[#F5F5F5] text-[#000000]"
        }`}
      >
        Yes
      </button>
      <button
        type="button"
        onClick={() => onChange("No")}
        className={`box-border shrink-0 w-[58px] 3xl:w-[66px] h-[41px] rounded-[100px] px-0 py-0 border border-none flex justify-center items-center font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] ${
          value === "No"
            ? "bg-[#FF4343] text-white"
            : "bg-[#F5F5F5] text-[#000000]"
        }`}
      >
        No
      </button>
    </div>
  </div>
);

const openNativePicker = (inputRef) => {
  const input = inputRef?.current;
  if (!input) return;

  if (typeof input.showPicker === "function") {
    try {
      input.showPicker();
      return;
    } catch {
      // Fall back to focusing the input when the native picker cannot open.
    }
  }

  input.focus();
};

const DATETIME_INPUT_CLASS =
  "admission-custom-datetime-input min-w-0 flex-1 bg-transparent border-none outline-none appearance-none font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#000000]";

const DateField = ({
  value,
  onChange,
  label,
  inputRef,
  min,
  max,
  alwaysShowLabel = false,
}) => (
  <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[18px] flex items-center gap-[8px]">
    <input
      ref={inputRef}
      type="date"
      value={value}
      min={min}
      max={max}
      onChange={(e) => onChange(e.target.value)}
      className={DATETIME_INPUT_CLASS}
    />
    {(alwaysShowLabel || value) && (
      <span className="font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] whitespace-nowrap min-w-0 truncate pointer-events-none">
        {label}
      </span>
    )}
    <button
      type="button"
      aria-label="Select date"
      className="ml-auto flex items-center cursor-pointer shrink-0 bg-transparent border-none p-0"
      onClick={() => openNativePicker(inputRef)}
    >
      <img src={calenderIcon} alt="" width={16} height={16} />
    </button>
  </div>
);

const ImageUploadField = ({ id, label, file, onChange }) => (
  <div className="w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[18px] flex items-center">
    <input
      type="file"
      accept="image/*"
      onChange={(e) => onChange(e.target.files?.[0] || null)}
      className="hidden"
      id={id}
    />
    <label
      htmlFor={id}
      className="w-full min-w-0 flex items-center justify-between gap-[8px] font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] cursor-pointer"
    >
      <span
        className={`min-w-0 truncate ${
          file ? "text-[#1A1A1A]" : "text-[#8B8B8B]"
        }`}
      >
        {file ? file.name : label}
      </span>
      <img
        src={uplaodIcon}
        alt=""
        width={16}
        height={16}
        className="shrink-0"
      />
    </label>
  </div>
);

const TimeField = ({ value, onChange, label, inputRef }) => (
  <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[18px] flex items-center gap-[8px]">
    <input
      ref={inputRef}
      type="time"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={DATETIME_INPUT_CLASS}
    />
    <span className="font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] whitespace-nowrap min-w-0 truncate pointer-events-none">
      {label}
    </span>
    <button
      type="button"
      aria-label="Select time"
      className="ml-auto flex items-center cursor-pointer shrink-0 bg-transparent border-none p-0"
      onClick={() => openNativePicker(inputRef)}
    >
      <img src={timeIcon} alt="" width={16} height={16} />
    </button>
  </div>
);

const AdmissionDetailsFormAi = () => {
  const [admissionDate, setAdmissionDate] = useState("");
  const [admissionTime, setAdmissionTime] = useState("");
  const [dischargeDate, setDischargeDate] = useState("");
  const [admissionType, setAdmissionType] = useState("");
  const [medicoLegalCase, setMedicoLegalCase] = useState("No");
  const [incidentDate, setIncidentDate] = useState("");
  const [firDate, setFirDate] = useState("");
  const [hospitalName, setHospitalName] = useState("");
  const [description, setDescription] = useState("");
  const [firReport, setFirReport] = useState(null);
  const [superintendentDeclaration, setSuperintendentDeclaration] =
    useState(null);
  const [initialAssessmentSheet, setInitialAssessmentSheet] = useState(null);
  const [emergencyCertificate, setEmergencyCertificate] = useState(null);
  const admissionTypeOptions = ["PLANNED", "EMERGENCY"];
  const admissionDateRef = useRef(null);
  const admissionTimeRef = useRef(null);
  const incidentDateRef = useRef(null);
  const firDateRef = useRef(null);
  const surgeryTreatmentDateRef = useRef(null);

  const getMinDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 2);
    return date.toISOString().split("T")[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  return (
    <div className="w-full">
      <style>{`
        .admission-custom-datetime-input::-webkit-calendar-picker-indicator {
          display: none !important;
          -webkit-appearance: none !important;
          appearance: none !important;
          opacity: 0 !important;
          width: 0 !important;
          height: 0 !important;
        }
        .admission-custom-datetime-input::-webkit-inner-spin-button,
        .admission-custom-datetime-input::-webkit-clear-button {
          display: none !important;
          -webkit-appearance: none !important;
        }
      `}</style>
      <div className="w-full h-0 border border-solid border-[#00000010] bg-[#00000010]" />{" "}
      <div className="grid grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-3 pt-[30px]">
        <DateField
          value={admissionDate}
          onChange={setAdmissionDate}
          label="Admission Date"
          inputRef={admissionDateRef}
          min={getMinDate()}
          max={getMaxDate()}
        />

        <TimeField
          value={admissionTime}
          onChange={setAdmissionTime}
          label="Admission Time"
          inputRef={admissionTimeRef}
        />

        <DateField
          value={dischargeDate}
          onChange={setDischargeDate}
          label="Surgery Treatment Date"
          inputRef={surgeryTreatmentDateRef}
          alwaysShowLabel
        />

        {/* Admission Type */}
        <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent">
          <select
            value={admissionType}
            onChange={(e) => {
              const nextType = e.target.value;
              setAdmissionType(nextType);
              if (nextType.toUpperCase() !== "EMERGENCY") {
                setInitialAssessmentSheet(null);
                setEmergencyCertificate(null);
              }
            }}
            className="absolute inset-0 w-full h-full pl-[32px] pr-[120px] bg-transparent border-none outline-none focus:outline-none focus:ring-0 appearance-none cursor-pointer font-lufga font-normal text-[14px] leading-[12px] tracking-[-0.03em] text-[#000000]"
          >
            {admissionTypeOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>

          {admissionType && (
            <span className="absolute right-[45px] top-1/2 -translate-y-1/2 font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] pointer-events-none">
              Admission Type
            </span>
          )}

          <div className="absolute right-[14px] top-1/2 -translate-y-1/2 pointer-events-none">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <path
                d="M7 10.5L14 17.5L21 10.5"
                stroke="#5AC69F"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        {admissionType.toUpperCase() === "EMERGENCY" && (
          <>
            <ImageUploadField
              id="initialAssessmentSheet"
              label="Initial Assessment/Treatment Sheet*"
              file={initialAssessmentSheet}
              onChange={setInitialAssessmentSheet}
            />
            <ImageUploadField
              id="emergencyCertificate"
              label="Emergency Certificate*"
              file={emergencyCertificate}
              onChange={setEmergencyCertificate}
            />
          </>
        )}

        <YesNoField
          label="Medico Legal Case"
          value={medicoLegalCase}
          onChange={setMedicoLegalCase}
        />

        {medicoLegalCase === "Yes" && (
          <>
            <DateField
              value={incidentDate}
              onChange={setIncidentDate}
              label="Incident Date"
              inputRef={incidentDateRef}
              alwaysShowLabel
            />

            <DateField
              value={firDate}
              onChange={setFirDate}
              label="FIR Date"
              inputRef={firDateRef}
              alwaysShowLabel
            />

            <div className="relative w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center">
              <input
                type="text"
                placeholder="Hospital Name"
                value={hospitalName}
                onChange={(e) => setHospitalName(e.target.value)}
                className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 shadow-none font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] placeholder:text-[#8B8B8B]"
              />
            </div>

            <div className="w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[18px] flex items-center">
              <input
                type="file"
                onChange={(e) => setFirReport(e.target.files[0])}
                className="hidden"
                id="firReport"
              />

              <label
                htmlFor="firReport"
                className="w-full min-w-0 flex items-center justify-between gap-[8px] font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] cursor-pointer"
              >
                <span className="min-w-0 truncate">
                  {firReport ? firReport.name : "Upload FIR report"}
                </span>
                <img src={uplaodIcon} alt="upload icon" className="shrink-0" />
              </label>
            </div>

            <div className="w-full min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent pl-[32px] pr-[18px] flex items-center">
              <input
                type="file"
                onChange={(e) =>
                  setSuperintendentDeclaration(e.target.files[0])
                }
                className="hidden"
                id="superintendentDeclaration"
              />

              <label
                htmlFor="superintendentDeclaration"
                className="w-full min-w-0 flex items-center justify-between gap-[8px] font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] text-[#8B8B8B] cursor-pointer"
              >
                <span className="min-w-0 truncate">
                  {superintendentDeclaration
                    ? superintendentDeclaration.name
                    : "Upload Superintendent Declaration"}
                </span>
                <img src={uplaodIcon} alt="upload icon" className="shrink-0" />
              </label>
            </div>

            <div className="col-span-2 min-w-0 box-border overflow-hidden h-[90px] xl:h-[52px] 2xl:h-[52px] 3xl:h-[52px] rounded-[42px] border border-solid border-[#D9D9D9] bg-transparent px-[32px] flex items-center">
              <input
                type="text"
                placeholder="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full min-w-0 bg-transparent border-none outline-none focus:outline-none focus:ring-0 shadow-none font-lufga font-normal text-[12px] leading-[12px] tracking-[-0.03em] placeholder:text-[#8B8B8B]"
              />
            </div>
          </>
        )}
      </div>
      <div className="flex justify-end mt-[12px]">
        <button className="w-[170px] h-[74px] xl:w-[94.97px] xl:h-[40.97px]  2xl:w-[94.97px] 2xl:h-[40.97px] border border-none rounded-full bg-black flex justify-center items-center gap-[8px] font-lufga text-[14px] text-[#68FF9F] font-normal">
          SAVE
          <img src={saveIcon} alt="save" className="pl-[16px]" />
        </button>
      </div>
    </div>
  );
};

export default AdmissionDetailsFormAi;
